import React, {
    useState,
    useEffect,
    useReducer,
    useContext,
    useRef,
} from "react";
// import { SocketContext } from "../../context/Socket/SocketContext";
import { toast } from "react-toastify";
import { useHistory } from "react-router-dom";

import { makeStyles } from "@material-ui/core/styles";
import { Box, Card, CardContent, Grid, Stack, Divider, Tooltip, Chip } from "@mui/material";
import Paper from "@material-ui/core/Paper";
import Button from "@material-ui/core/Button";
import Avatar from "@material-ui/core/Avatar";
import { Facebook, Instagram, WhatsApp } from "@material-ui/icons";
import SearchIcon from "@material-ui/icons/Search";

import TextField from "@material-ui/core/TextField";
import InputBase from "@material-ui/core/InputBase";
import InputAdornment from "@material-ui/core/InputAdornment";
import Typography from "@material-ui/core/Typography";

import IconButton from "@material-ui/core/IconButton";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import CancelIcon from "@material-ui/icons/Cancel";
import BlockIcon from "@material-ui/icons/Block";
import AddIcon from "@material-ui/icons/Add";

import api from "../../services/api";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import ContactModal from "../../components/ContactModal";
import ConfirmationModal from "../../components/ConfirmationModal";

import { i18n } from "../../translate/i18n";
import toastError from "../../errors/toastError";

import { AuthContext } from "../../context/Auth/AuthContext";
import { Can } from "../../components/Can";
import NewTicketModal from "../../components/NewTicketModal";
import { v4 as uuidv4 } from "uuid";

import ContactImportWpModal from "../../components/ContactImportWpModal";
import ContactLidModal from "../../components/ContactLidModal";
import useCompanySettings from "../../hooks/useSettings/companySettings";
import { TicketsContext } from "../../context/Tickets/TicketsContext";

// Phone number validation and formatting functions
const isValidBrazilianPhoneNumber = (number) => {
    if (!number) return false;

    // Remove all non-numeric characters
    const cleanNumber = number.replace(/\D/g, '');

    // Check if the number is empty or too short
    if (!cleanNumber || cleanNumber.length < 10) return false;

    // Brazilian numbers typically have 10-11 digits (with area code)
    // DDD (area code) in Brazil is 2 digits (11-99)
    if (cleanNumber.length > 13) return false;

    // Check if starts with country code 55 (Brazil) or has valid area code
    const hasBrazilCountryCode = cleanNumber.startsWith('55');
    const numberWithoutCountryCode = hasBrazilCountryCode ? cleanNumber.slice(2) : cleanNumber;

    // Extract area code (DDD)
    const areaCode = numberWithoutCountryCode.slice(0, 2);

    // Validate the area code (Brazilian DDDs range from 11 to 99)
    const areaCodeNum = parseInt(areaCode, 10);
    if (areaCodeNum < 11 || areaCodeNum > 99) return false;

    // Check the length of the remaining phone number
    const phoneNumberWithoutAreaCode = numberWithoutCountryCode.slice(2);

    // Brazilian mobile numbers have 9 digits, landlines have 8
    const isValidLength = phoneNumberWithoutAreaCode.length === 8 || phoneNumberWithoutAreaCode.length === 9;

    // Mobile numbers in Brazil start with 9
    const isMobileValid = phoneNumberWithoutAreaCode.length === 9 && phoneNumberWithoutAreaCode.startsWith('9');

    // Landline numbers should have 8 digits
    const isLandlineValid = phoneNumberWithoutAreaCode.length === 8;

    return isValidLength && (isMobileValid || isLandlineValid);
};

// Function to format the Brazilian phone number for display
const formatBrazilianPhoneNumber = (number) => {
    if (!isValidBrazilianPhoneNumber(number)) return null;

    // Remove all non-numeric characters
    const cleanNumber = number.replace(/\D/g, '');

    // Handle numbers with country code
    const hasBrazilCountryCode = cleanNumber.startsWith('55');
    const numberWithoutCountryCode = hasBrazilCountryCode ? cleanNumber.slice(2) : cleanNumber;

    // Get area code
    const areaCode = numberWithoutCountryCode.slice(0, 2);

    // Get the phone part
    const phoneNumber = numberWithoutCountryCode.slice(2);

    // Format based on mobile (9 digits) or landline (8 digits)
    if (phoneNumber.length === 9) {
        // Mobile format: (XX) 9XXXX-XXXX
        return `🇧🇷 (${areaCode}) ${phoneNumber.slice(0, 1)}${phoneNumber.slice(1, 5)}-${phoneNumber.slice(5)}`;
    } else {
        // Landline format: (XX) XXXX-XXXX
        return `🇧🇷 (${areaCode}) ${phoneNumber.slice(0, 4)}-${phoneNumber.slice(4)}`;
    }
};

// Format phone number with LGPD masking when needed
const formatPhoneNumber = (number, isGroup, shouldHide = false, userProfile = "") => {
    // Handle group numbers differently
    if (isGroup) return number;

    // Check if it's a valid Brazilian phone number
    const isValidBrNumber = isValidBrazilianPhoneNumber(number);

    // If not valid, return the original number instead of hiding it
    if (!isValidBrNumber) return number;

    // If LGPD is enabled and number should be hidden for user profile
    if (shouldHide && userProfile === "user") {
        const formattedNumber = formatBrazilianPhoneNumber(number);
        if (!formattedNumber) return null;

        // Ensure proper masking of the phone number
        const parts = formattedNumber.split(' ');
        if (parts.length >= 3) {
            const lastPart = parts[parts.length - 1];
            const [firstHalf, secondHalf] = lastPart.split('-');
            return `🇧🇷 ${parts[1]} ${firstHalf.slice(0, -2)}**-**${secondHalf.slice(-2)}`;
        }
        return formattedNumber;
    }

    // Return properly formatted Brazilian phone number
    return formatBrazilianPhoneNumber(number) || null;
};

const reducer = (state, action) => {
    if (action.type === "LOAD_CONTACTS") {
        const contacts = action.payload;
        const newContacts = [];
        const newState = [...state];

        contacts.forEach((contact) => {
            const contactIndex = newState.findIndex((c) => c.id === contact.id);
            if (contactIndex !== -1) {
                newState[contactIndex] = contact;
            } else {
                newContacts.push(contact);
            }
        });

        return [...newState, ...newContacts];
    }

    if (action.type === "UPDATE_CONTACTS") {
        const contact = action.payload;
        const contactIndex = state.findIndex((c) => c.id === contact.id);

        if (contactIndex !== -1) {
            const newState = [...state];
            newState[contactIndex] = contact;
            return newState;
        } else {
            return [contact, ...state];
        }
    }

    if (action.type === "DELETE_CONTACT") {
        const contactId = action.payload;

        const contactIndex = state.findIndex((c) => c.id === contactId);
        if (contactIndex !== -1) {
            const newState = [...state];
            newState.splice(contactIndex, 1);
            return newState;
        }
        return state;
    }

    if (action.type === "RESET") {
        return [];
    }
};

const useStyles = makeStyles((theme) => ({
    mainContainer: {
        display: "flex",
        flexDirection: "column",
        height: "100%",
        backgroundColor: theme.palette.type === 'light' ? '#f5f5f5' : theme.palette.background.default,
        padding: theme.spacing(4),
        overflow: "hidden",
    },
    header: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        marginBottom: theme.spacing(4),
    },
    titleContainer: {
        display: "flex",
        flexDirection: "column",
    },
    title: {
        fontWeight: 800,
        color: theme.palette.primary.main,
        fontSize: "2.125rem",
        lineHeight: 1.235,
        marginBottom: theme.spacing(0.5),
    },
    subtitle: {
        color: theme.palette.text.secondary,
        fontSize: "0.875rem",
    },
    headerActions: {
        display: "flex",
        alignItems: "center",
        gap: theme.spacing(2),
    },
    searchContainer: {
        display: "flex",
        alignItems: "center",
        backgroundColor: theme.palette.background.paper,
        borderRadius: "50px",
        padding: "6px 20px",
        width: "300px",
        boxShadow: "0px 2px 4px -1px rgba(0,0,0,0.2)",
        border: `1px solid ${theme.palette.divider}`,
        transition: "all 0.3s ease",
        "&:focus-within": {
            boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
            borderColor: theme.palette.primary.main,
        },
    },
    searchInput: {
        marginLeft: theme.spacing(1),
        flex: 1,
        fontSize: "0.95rem",
    },
    searchIcon: {
        color: theme.palette.text.secondary,
    },
    addButton: {
        borderRadius: "50px",
        padding: "8px 24px",
        fontWeight: "bold",
        textTransform: "none",
        boxShadow: theme.shadows[3],
        "&:hover": {
            boxShadow: theme.shadows[6],
        },
    },
    paperTable: {
        flex: 1,
        overflowY: "auto",
        width: "100%",
        padding: theme.spacing(2),
        backgroundColor: "transparent",
        boxShadow: "none",
        border: "none",
        "&::-webkit-scrollbar": {
            display: "none",
        },
        "-ms-overflow-style": "none",
        "scrollbar-width": "none",
    },
    connectionTag: {
        background: "green",
        color: "#FFF",
        marginRight: 1,
        padding: 5,
        fontWeight: 'bold',
        borderRadius: 5,
        fontSize: "0.6em",
    }
}));

const Contacts = () => {
    const classes = useStyles();
    const history = useHistory();

    //   const socketManager = useContext(SocketContext);
    const { user, socket } = useContext(AuthContext);

    const [loading, setLoading] = useState(false);
    const [pageNumber, setPageNumber] = useState(1);
    const [searchParam, setSearchParam] = useState("");
    const [contacts, dispatch] = useReducer(reducer, []);
    const [selectedContactId, setSelectedContactId] = useState(null);
    const [contactModalOpen, setContactModalOpen] = useState(false);
    const [contactLidModalOpen, setContactLidModalOpen] = useState(false);

    const [importContactModalOpen, setImportContactModalOpen] = useState(false);
    const [deletingContact, setDeletingContact] = useState(null);
    const [ImportContacts, setImportContacts] = useState(null);
    const [blockingContact, setBlockingContact] = useState(null);
    const [unBlockingContact, setUnBlockingContact] = useState(null);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [exportContact, setExportContact] = useState(false);
    const [confirmChatsOpen, setConfirmChatsOpen] = useState(false);
    const [hasMore, setHasMore] = useState(false);
    const [newTicketModalOpen, setNewTicketModalOpen] = useState(false);
    const [contactTicket, setContactTicket] = useState({});
    const fileUploadRef = useRef(null);
    const [selectedTags, setSelectedTags] = useState([]);
    const { setCurrentTicket } = useContext(TicketsContext);

    const { getAll: getAllSettings } = useCompanySettings();
    const [hideNum, setHideNum] = useState(false);
    const [enableLGPD, setEnableLGPD] = useState(false);
    useEffect(() => {

        async function fetchData() {

            const settingList = await getAllSettings(user.companyId);

            for (const [key, value] of Object.entries(settingList)) {

                if (key === "enableLGPD") setEnableLGPD(value === "enabled");
                if (key === "lgpdHideNumber") setHideNum(value === "enabled");

            }
        }
        fetchData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const handleImportExcel = async () => {
        try {
            const formData = new FormData();
            formData.append("file", fileUploadRef.current.files[0]);
            await api.request({
                url: `/contacts/upload`,
                method: "POST",
                data: formData,
            });
            history.go(0);
        } catch (err) {
            toastError(err);
        }
    };

    useEffect(() => {
        dispatch({ type: "RESET" });
        setPageNumber(1);
    }, [searchParam, selectedTags]);

    useEffect(() => {
        setLoading(true);
        const delayDebounceFn = setTimeout(() => {
            const fetchContacts = async () => {
                try {
                    const { data } = await api.get("/contacts/", {
                        params: { searchParam, pageNumber, contactTag: JSON.stringify(selectedTags) },
                    });
                    dispatch({ type: "LOAD_CONTACTS", payload: data.contacts });
                    setHasMore(data.hasMore);
                    setLoading(false);
                } catch (err) {
                    toastError(err);
                }
            };
            fetchContacts();
        }, 500);
        return () => clearTimeout(delayDebounceFn);
    }, [searchParam, pageNumber, selectedTags]);

    useEffect(() => {
        const companyId = user.companyId;
        //    const socket = socketManager.GetSocket();

        const onContactEvent = (data) => {
            if (data.action === "update" || data.action === "create") {
                dispatch({ type: "UPDATE_CONTACTS", payload: data.contact });
            }

            if (data.action === "delete") {
                dispatch({ type: "DELETE_CONTACT", payload: +data.contactId });
            }
        };
        socket.on(`company-${companyId}-contact`, onContactEvent);

        return () => {
            socket.off(`company-${companyId}-contact`, onContactEvent);
        };
    }, [socket, user.companyId]);

    const handleSelectTicket = (ticket) => {
        const code = uuidv4();
        const { id, uuid } = ticket;
        setCurrentTicket({ id, uuid, code });
    }

    const handleCloseOrOpenTicket = (ticket) => {
        setNewTicketModalOpen(false);
        if (ticket !== undefined && ticket.uuid !== undefined) {
            handleSelectTicket(ticket);
            history.push(`/tickets/${ticket.uuid}`);
        }
    };

    const handleSelectedTags = (selecteds) => {
        const tags = selecteds.map((t) => t.id);
        setSelectedTags(tags);
    };

    const handleSearch = (event) => {
        setSearchParam(event.target.value.toLowerCase());
    };

    const handleOpenContactModal = () => {
        setSelectedContactId(null);
        setContactModalOpen(true);
    };

    const handleCloseContactModal = () => {
        setSelectedContactId(null);
        setContactModalOpen(false);
    };

    const handleOpenContactLidModal = () => {
        setContactLidModalOpen(true);
    };

    const handleCloseContactLidModal = () => {
        setContactLidModalOpen(false);
    };

    const hadleEditContact = (contactId) => {
        setSelectedContactId(contactId);
        setContactModalOpen(true);
    };

    const handleDeleteContact = async (contactId) => {
        try {
            await api.delete(`/contacts/${contactId}`);
            toast.success(i18n.t("contacts.toasts.deleted"));
        } catch (err) {
            toastError(err);
        }
        setDeletingContact(null);
        setSearchParam("");
        setPageNumber(1);
    };

    const handleBlockContact = async (contactId) => {
        try {
            await api.put(`/contacts/block/${contactId}`, { active: false });
            toast.success("Contato bloqueado");
        } catch (err) {
            toastError(err);
        }
        setDeletingContact(null);
        setSearchParam("");
        setPageNumber(1);
        setBlockingContact(null);
    };

    const handleUnBlockContact = async (contactId) => {
        try {
            await api.put(`/contacts/block/${contactId}`, { active: true });
            toast.success("Contato desbloqueado");
        } catch (err) {
            toastError(err);
        }
        setDeletingContact(null);
        setSearchParam("");
        setPageNumber(1);
        setUnBlockingContact(null);
    };

    const handleimportContact = async () => {
        try {
            await api.post("/contacts/import");
            history.go(0);
            setImportContacts(false);
        } catch (err) {
            toastError(err);
            setImportContacts(false);
        }
    };

    const handleimportChats = async () => {
        try {
            await api.post("/contacts/import/chats");
            history.go(0);
        } catch (err) {
            toastError(err);
        }
    };

    const loadMore = () => {
        setPageNumber((prevState) => prevState + 1);
    };

    const handleScroll = (e) => {
        if (!hasMore || loading) return;
        const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
        if (scrollHeight - (scrollTop + 100) < clientHeight) {
            loadMore();
        }
    };

    return (
        <div className={classes.mainContainer}>
            <NewTicketModal
                modalOpen={newTicketModalOpen}
                initialContact={contactTicket}
                onClose={(ticket) => {
                    handleCloseOrOpenTicket(ticket);
                }}
            />
            <ContactModal
                open={contactModalOpen}
                onClose={handleCloseContactModal}
                aria-labelledby="form-dialog-title"
                contactId={selectedContactId}
            ></ContactModal>

            <ContactLidModal
                open={contactLidModalOpen}
                onClose={handleCloseContactLidModal}
            />

            <ConfirmationModal
                title={
                    deletingContact
                        ? `${i18n.t(
                            "contacts.confirmationModal.deleteTitle"
                        )} ${deletingContact.name}?`
                        : blockingContact
                            ? `Bloquear Contato ${blockingContact.name}?`
                            : unBlockingContact
                                ? `Desbloquear Contato ${unBlockingContact.name}?`
                                : ImportContacts
                                    ? `${i18n.t("contacts.confirmationModal.importTitlte")}`
                                    : `${i18n.t("contactListItems.confirmationModal.importTitlte")}`
                }
                open={confirmOpen}
                onClose={setConfirmOpen}
                onConfirm={(e) =>
                    deletingContact
                        ? handleDeleteContact(deletingContact.id)
                        : blockingContact
                            ? handleBlockContact(blockingContact.id)
                            : unBlockingContact
                                ? handleUnBlockContact(unBlockingContact.id)
                                : ImportContacts
                                    ? handleimportContact()
                                    : handleImportExcel()
                }
            >
                {exportContact
                    ?
                    `${i18n.t("contacts.confirmationModal.exportContact")}`
                    : deletingContact
                        ? `${i18n.t("contacts.confirmationModal.deleteMessage")}`
                        : blockingContact
                            ? `${i18n.t("contacts.confirmationModal.blockContact")}`
                            : unBlockingContact
                                ? `${i18n.t("contacts.confirmationModal.unblockContact")}`
                                : ImportContacts
                                    ? `${i18n.t("contacts.confirmationModal.importMessage")}`
                                    : `${i18n.t(
                                        "contactListItems.confirmationModal.importMessage"
                                    )}`}
            </ConfirmationModal>
            <ConfirmationModal
                title={i18n.t("contacts.confirmationModal.importChat")}
                open={confirmChatsOpen}
                onClose={setConfirmChatsOpen}
                onConfirm={(e) => handleimportChats()}
            >
                {i18n.t("contacts.confirmationModal.wantImport")}
            </ConfirmationModal>

            <div className={classes.header}>
                <div className={classes.titleContainer}>
                    <Typography className={classes.title}>
                        {i18n.t("contacts.title")} ({contacts.length})
                    </Typography>
                    <Typography className={classes.subtitle}>
                        Gerencie seus contatos e clientes
                    </Typography>
                </div>

                <div className={classes.headerActions}>
                    <div className={classes.searchContainer}>
                        <SearchIcon className={classes.searchIcon} />
                        <InputBase
                            placeholder={i18n.t("contacts.searchPlaceholder")}
                            classes={{
                                root: classes.searchInput,
                            }}
                            value={searchParam}
                            onChange={handleSearch}
                        />
                    </div>

                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleOpenContactLidModal}
                        className={classes.addButton}
                        startIcon={<AddIcon />}
                    >
                        {i18n.t("contacts.buttons.add")}
                    </Button>
                </div>
            </div>

            {importContactModalOpen && (
                <ContactImportWpModal
                    isOpen={importContactModalOpen}
                    handleClose={() => setImportContactModalOpen(false)}
                    selectedTags={selectedTags}
                    hideNum={hideNum}
                    userProfile={user.profile}
                />
            )}
            <Paper
                className={classes.paperTable}
                variant="outlined"
                onScroll={handleScroll}
            >
                <>
                    <input
                        style={{ display: "none" }}
                        id="upload"
                        name="file"
                        type="file"
                        accept=".xls,.xlsx"
                        onChange={() => {
                            setConfirmOpen(true);
                        }}
                        ref={fileUploadRef}
                    />
                </>
                <Grid container spacing={3}>
                    {contacts.map((contact) => {
                        const formattedNumber = formatPhoneNumber(
                            contact.number,
                            contact.isGroup,
                            enableLGPD && hideNum,
                            user.profile
                        );

                        if (!formattedNumber) return null;

                        return (
                            <Grid item xs={12} sm={6} md={4} lg={3} key={contact.id}>
                                <Card
                                    sx={{
                                        position: "relative",
                                        height: "100%",
                                        display: "flex",
                                        flexDirection: "column",
                                        borderRadius: "16px",
                                        boxShadow: "0 4px 6px rgba(0, 0, 0, 0.05)",
                                        transition: "all 0.3s ease",
                                        border: "1px solid",
                                        borderColor: "divider",
                                        "&:hover": {
                                            transform: "translateY(-4px)",
                                            boxShadow: "0 10px 20px rgba(0,0,0,0.1)",
                                        },
                                        opacity: contact.active ? 1 : 0.6,
                                    }}
                                >
                                    <CardContent sx={{ flexGrow: 1, p: 3 }}>
                                        <Box display="flex" alignItems="center" mb={2}>
                                            <Avatar
                                                src={`${contact?.urlPicture}`}
                                                sx={{ width: 56, height: 56, mr: 2 }}
                                            />
                                            <Box sx={{ overflow: "hidden" }}>
                                                <Typography variant="h6" fontWeight="bold" noWrap title={contact.name}>
                                                    {contact.name}
                                                </Typography>
                                                {!contact.active && (
                                                    <Chip
                                                        label="Desativado"
                                                        size="small"
                                                        color="error"
                                                        sx={{ height: 20, fontSize: "0.7rem", fontWeight: "bold", mt: 0.5 }}
                                                    />
                                                )}
                                            </Box>
                                        </Box>

                                        <Box display="flex" alignItems="center" gap={1} mb={2}>
                                            <WhatsApp fontSize="small" sx={{ color: "#25D366" }} />
                                            <Typography variant="body2" color="textSecondary" noWrap>
                                                {formattedNumber}
                                            </Typography>
                                        </Box>
                                    </CardContent>

                                    <Divider />
                                    
                                    <Box
                                        sx={{
                                            display: "flex",
                                            justifyContent: "space-between",
                                            alignItems: "center",
                                            p: 1.5,
                                            bgcolor: "background.default",
                                        }}
                                    >
                                        <Box>
                                            <Tooltip title="Iniciar Atendimento" arrow>
                                                <span>
                                                    <IconButton
                                                        size="small"
                                                        disabled={!contact.active}
                                                        onClick={() => {
                                                            setContactTicket(contact);
                                                            setNewTicketModalOpen(true);
                                                        }}
                                                        sx={{ color: contact.channel === "whatsapp" ? "green" : contact.channel === "instagram" ? "purple" : "blue" }}
                                                    >
                                                        {contact.channel === "whatsapp" && <WhatsApp fontSize="small" />}
                                                        {contact.channel === "instagram" && <Instagram fontSize="small" />}
                                                        {contact.channel === "facebook" && <Facebook fontSize="small" />}
                                                    </IconButton>
                                                </span>
                                            </Tooltip>

                                        </Box>

                                        <Can
                                            role={user.profile}
                                            perform="contacts-page:deleteContact"
                                            yes={() => (
                                                <Tooltip title="Excluir" arrow>
                                                    <IconButton
                                                        size="small"
                                                        color="error"
                                                        onClick={(e) => {
                                                            setConfirmOpen(true);
                                                            setDeletingContact(contact);
                                                        }}
                                                    >
                                                        <DeleteOutlineIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                        />
                                    </Box>
                                </Card>
                            </Grid>
                        );
                    })}
                    {loading && (
                        Array.from(new Array(4)).map((_, i) => (
                            <Grid item xs={12} sm={6} md={4} lg={3} key={i}>
                                <Card sx={{ height: 180, borderRadius: "16px", p: 2 }}>
                                    <Box display="flex" alignItems="center">
                                        <div style={{ width: 56, height: 56, borderRadius: "50%", backgroundColor: "rgba(0,0,0,0.1)", marginRight: 16 }} />
                                        <div style={{ width: "60%", height: 20, backgroundColor: "rgba(0,0,0,0.1)", borderRadius: 4 }} />
                                    </Box>
                                </Card>
                            </Grid>
                        ))
                    )}
                </Grid>
            </Paper>
        </div>
    );
};

export default Contacts;